import { wbStandard, type WbAenderung, type WbElement, type WbFelder, type WbVomServer, type WbVonClient } from '@beholder/shared/module/whiteboard';

/**
 * Zufällige ID (20 Zeichen hex). Nicht crypto.randomUUID: das gibt es nur über HTTPS/localhost – beim Aufruf über
 * http://<LAN-Adresse> fehlt es, jeder neue Strich warf dann einen Fehler und verschwand sofort wieder.
 */
export function neueId(): string {
  const b = crypto.getRandomValues(new Uint8Array(10));
  let s = '';
  for (const x of b) s += x.toString(16).padStart(2, '0');
  return s;
}

interface Offen {
  tx: string;
  aenderungen: WbAenderung[];
}

interface UndoEintrag {
  vorher: WbAenderung[];
  nachher: WbAenderung[];
}

function anwenden(m: Map<string, WbElement>, a: WbAenderung) {
  const alt = m.get(a.id);
  if (alt) m.set(a.id, { ...alt, ...a.felder, art: alt.art });
  else if (a.felder.art) m.set(a.id, { ...wbStandard(a.felder.art, a.id), ...a.felder });
}

/**
 * Abgleich einer Tafel mit dem Server.
 * `basis` ist der vom Server bestätigte Stand; eigene, noch nicht bestätigte Änderungen (`offen`) werden darüber gelegt.
 * Kommt eine fremde Änderung an, bleibt die eigene oben, bis der Server sie bestätigt – so geht nichts verloren
 * und am Ende sehen alle denselben Stand. Änderungen sind Feld-Zuweisungen und dürfen deshalb gefahrlos wiederholt werden.
 */
export class TafelSync {
  private basis = new Map<string, WbElement>();
  private offen: Offen[] = [];
  private undo: UndoEintrag[] = [];
  private redo: UndoEintrag[] = [];
  private cache: Map<string, WbElement> | null = null;
  private hoerer = new Set<() => void>();
  /** Zählt bei jeder Änderung hoch (für React). */
  stand = 0;
  geladen = false;

  constructor(private senden: (d: WbVonClient) => void) {}

  abo = (h: () => void) => {
    this.hoerer.add(h);
    return () => void this.hoerer.delete(h);
  };

  private melden() {
    this.cache = null;
    this.stand++;
    this.hoerer.forEach((h) => h());
  }

  /** Aktueller Stand inkl. eigener offener Änderungen (auch gelöschte Elemente). */
  ansicht(): Map<string, WbElement> {
    if (!this.cache) {
      const m = new Map(this.basis);
      for (const o of this.offen) for (const a of o.aenderungen) anwenden(m, a);
      this.cache = m;
    }
    return this.cache;
  }

  get(id: string): WbElement | undefined {
    const e = this.ansicht().get(id);
    return e && !e.geloescht ? e : undefined;
  }

  get kannRueckgaengig() {
    return this.undo.length > 0;
  }
  get kannWiederholen() {
    return this.redo.length > 0;
  }
  get wartet() {
    return this.offen.length;
  }

  /** Nachricht vom Server verarbeiten. Liefert einen Fehlertext, wenn eine eigene Änderung abgelehnt wurde. */
  empfangen(d: WbVomServer): string | null {
    if (d.typ === 'zustand') {
      this.basis = new Map(d.elemente.map((e) => [e.id, e]));
      this.geladen = true;
      // Nach einer Unterbrechung: noch nicht bestätigte Änderungen erneut schicken (doppelt schadet nicht)
      for (const o of this.offen) this.senden({ typ: 'aendern', tx: o.tx, aenderungen: o.aenderungen });
      this.melden();
    } else if (d.typ === 'geaendert') {
      for (const e of d.elemente) {
        const alt = this.basis.get(e.id);
        if (!alt || e.version >= alt.version) this.basis.set(e.id, e);
      }
      const i = this.offen.findIndex((o) => o.tx === d.tx);
      if (i >= 0) this.offen.splice(i, 1);
      this.melden();
    } else if (d.typ === 'abgelehnt') {
      const i = this.offen.findIndex((o) => o.tx === d.tx);
      if (i >= 0) this.offen.splice(i, 1);
      this.melden();
      return d.text;
    }
    return null;
  }

  /** Änderung sofort zeigen und an den Server schicken (ohne Rückgängig-Eintrag, z. B. während des Ziehens). */
  lokal(aenderungen: WbAenderung[]) {
    if (!aenderungen.length) return;
    const tx = neueId();
    this.offen.push({ tx, aenderungen });
    this.senden({ typ: 'aendern', tx, aenderungen });
    this.melden();
  }

  /** Vorheriger Stand der angegebenen Felder (für Rückgängig). */
  vorherVon(aenderungen: WbAenderung[]): WbAenderung[] {
    const m = this.ansicht();
    return aenderungen.map((a) => {
      const alt = m.get(a.id);
      if (!alt || (alt.geloescht && a.felder.geloescht === false)) return { id: a.id, felder: { geloescht: true } };
      const felder: WbFelder = {};
      for (const k of Object.keys(a.felder) as (keyof WbFelder)[]) {
        if (k === 'art') continue;
        (felder as Record<string, unknown>)[k] = alt[k];
      }
      return { id: a.id, felder };
    });
  }

  /** Änderung mit Rückgängig-Eintrag. `vorher` angeben, wenn der Ausgangsstand schon früher festgehalten wurde (Ziehen). */
  aendern(nachher: WbAenderung[], vorher?: WbAenderung[]) {
    if (!nachher.length) return;
    const v = vorher ?? this.vorherVon(nachher);
    this.lokal(nachher);
    this.undo.push({ vorher: v, nachher });
    if (this.undo.length > 200) this.undo.shift();
    this.redo = [];
  }

  rueckgaengig() {
    const e = this.undo.pop();
    if (!e) return;
    this.lokal(e.vorher);
    this.redo.push(e);
  }

  wiederholen() {
    const e = this.redo.pop();
    if (!e) return;
    this.lokal(e.nachher);
    this.undo.push(e);
  }
}
